// @ts-check
// Lista da Caixa de atendimento (contrato 10 de atendimento, seção 24.2): abas com contadores ("Meus", "Sem
// responsável", "Departamento", "Todos", "Com bot", "Aguardando a família", "Vencendo", "Fechados"), busca por protocolo,
// nome e assunto, filtros de situação, prioridade, categoria, unidade, canal e prazo (na URL) e os itens com quem fala,
// protocolo, assunto, departamento, chip do prazo, canal, não lidas e os selos "Bot" e "Restrito".
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { INBOX_FILTERS, countActiveInboxFilters, inboxItemKey } from '../../lib/service-inbox-rules.js';
import { getThreadDisplayName } from '../conversations/conversation-display.js';
import { MemberAvatar } from '../conversations/conversation-assignee.js';
import { formatThreadListTime } from '../../lib/conversation-format.js';
import { Avatar } from '../../ui/avatar.js';
import { Button, LightButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSectionLabel, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { RestrictedBadge, ServiceChannelIcon, SlaChip, ticketValueLabel } from './service-display.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/service-desk.js').InboxItem} InboxItem
 * @typedef {import('../../lib/service-inbox-rules.js').InboxFilters} InboxFilters
 * @typedef {import('../../lib/service-inbox-rules.js').InboxView} InboxView
 */

/** Ícone de cada aba. */
const VIEW_ICONS = Object.freeze(/** @type {Record<string, string>} */ ({
  mine: 'IconUser', unassigned: 'IconUserPlus', team: 'IconUsersGroup', all: 'IconInbox', bot: 'IconRobot', waitingFamily: 'IconPlayerPause', atRisk: 'IconAlertTriangle', closed: 'IconCircleCheck',
}));

/** @param {{ item: InboxItem, isSelected: boolean, onSelect: () => void }} props */
function InboxRow({ item, isSelected, onSelect }) {
  const { ticket, thread } = item;
  const speaker = thread?.identifiedPerson?.name ?? (thread ? getThreadDisplayName(thread) : ticket?.requester?.name ?? ticket?.household?.name ?? t('serviceDesk.list.noRequester'));
  const time = thread?.lastMessage?.sentAt ?? ticket?.lastFamilyMessageAt ?? ticket?.updatedAt ?? thread?.updatedAt;
  const unread = thread?.unreadCount ?? 0;
  const isBot = thread?.botState === 'BOT';
  const preview = thread?.isLastMessageRestricted
    ? t('serviceDesk.thread.restrictedPreview')
    : thread?.lastMessage?.text?.trim() || ticket?.summary || '';
  return html`<li>
    <button
      type="button"
      class="service-inbox-row"
      data-selected=${isSelected ? 'true' : undefined}
      data-unread=${unread > 0 ? 'true' : undefined}
      aria-current=${isSelected ? 'true' : undefined}
      data-testid="service-inbox-row"
      onClick=${onSelect}
    >
      <span class="service-inbox-row-avatar">
        <${Avatar} placeholder=${speaker} seed=${thread?.person?.id ?? ticket?.requester?.id ?? ticket?.id ?? speaker} size="lg" type="rounded" />
      </span>
      <span class="service-inbox-row-texts">
        <span class="service-inbox-row-top">
          <span class="service-inbox-row-name">${speaker}</span>
          ${isBot ? html`<span class="service-badge" data-kind="bot"><${Icon} name="IconRobot" size=${12} />${t('serviceDesk.badges.bot')}</span>` : null}
          <${RestrictedBadge} ticket=${ticket} />
          <span class="service-inbox-row-time" data-unread=${unread > 0 ? 'true' : undefined}>${time ? formatThreadListTime(time) : ''}</span>
        </span>
        <span class="service-inbox-row-subject">
          ${ticket ? html`<span class="service-protocol">${ticket.protocol}</span>` : html`<span class="service-protocol" data-empty="true">${t('serviceDesk.list.noTicket')}</span>`}
          <span class="service-inbox-row-subject-text">${ticket?.subject ?? t('serviceDesk.list.queueThread')}</span>
        </span>
        <span class="service-inbox-row-preview">${preview}</span>
        <span class="service-inbox-row-meta">
          <${ServiceChannelIcon} channel=${ticket?.channel ?? thread?.channelType ?? null} size=${12} />
          ${ticket?.team?.name ?? thread?.queueTeam?.name ? html`<span class="service-inbox-row-team">${ticket?.team?.name ?? thread?.queueTeam?.name}</span>` : null}
          ${ticket ? html`<${SlaChip} ticket=${ticket} compact=${true} />` : null}
          <span class="service-inbox-row-spacer"></span>
          ${unread > 0 ? html`<span class="service-unread" aria-label=${t('serviceDesk.list.unread', { count: unread })}>${unread}</span>` : null}
          ${ticket?.assignee
            ? html`<span class="service-inbox-row-assignee" title=${t('serviceDesk.list.assignedTo', { name: ticket.assignee.name })}>
                <${MemberAvatar} name=${ticket.assignee.name} seed=${ticket.assignee.workspaceMemberId} />
              </span>`
            : null}
        </span>
      </span>
    </button>
  </li>`;
}

/**
 * Opções de um filtro: os valores fixos do atendimento ou, para a unidade, as unidades dos metadados.
 * @param {{ key: string, values?: readonly string[] }} filter
 * @returns {Array<{ value: string, label: string }>}
 */
function filterOptions(filter) {
  if (filter.key === 'campusId') return [];
  const field = { status: 'status', priority: 'priority', category: 'category', channelType: 'channel', slaStatus: 'slaStatus' }[filter.key] ?? filter.key;
  return (filter.values ?? []).map((value) => ({ value, label: ticketValueLabel(field, value) }));
}

/**
 * Botão "Filtros" com um menu por filtro; o selo diz quantos estão ligados.
 * @param {{ filters: InboxFilters, onChange: (changes: Partial<InboxFilters>) => void, campuses: Array<{ id: string, name: string }> }} props
 */
function InboxFilterMenu({ filters, onChange, campuses }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [openFilterKey, setOpenFilterKey] = useState(/** @type {string | null} */ (null));
  const activeCount = countActiveInboxFilters(filters);
  const filterList = INBOX_FILTERS.filter((filter) => filter.key !== 'teamId' && (filter.key !== 'campusId' || campuses.length > 0));
  const openFilter = filterList.find((filter) => filter.key === openFilterKey) ?? null;
  const options = openFilter ? (openFilter.key === 'campusId' ? campuses.map((campus) => ({ value: campus.id, label: campus.name })) : filterOptions(openFilter)) : [];
  return html`<div class="service-filter-menu">
    <button type="button" ref=${anchorRef} class="service-filter-button" data-active=${activeCount > 0 ? 'true' : undefined}
      aria-haspopup="menu" aria-expanded=${isOpen ? 'true' : 'false'} onClick=${() => {
        setIsOpen(!isOpen);
        setOpenFilterKey(null);
      }}>
      <${Icon} name="IconFilter" size=${14} />
      <span>${t('serviceDesk.list.filters')}</span>
      ${activeCount > 0 ? html`<span class="pill">${activeCount}</span>` : null}
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${240} placement="bottom-end" role="menu">
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${openFilter
          ? html`
              <${MenuItem} text=${t('serviceDesk.list.backToFilters')} icon="IconChevronLeft" onClick=${() => setOpenFilterKey(null)} />
              <${DropdownMenuSectionLabel} label=${t(`serviceDesk.list.filterLabels.${openFilter.key}`)} />
              <${MenuItem} text=${t('serviceDesk.list.anyValue')} selected=${!(/** @type {any} */ (filters)[openFilter.key])}
                onClick=${() => onChange({ [openFilter.key]: null })} />
              ${options.map((option) => html`<${MenuItem} key=${option.value} text=${option.label}
                selected=${/** @type {any} */ (filters)[openFilter.key] === option.value}
                onClick=${() => {
                  onChange({ [openFilter.key]: option.value });
                  setIsOpen(false);
                }} />`)}
            `
          : html`
              ${filterList.map((filter) => {
                const value = /** @type {any} */ (filters)[filter.key];
                const label = value
                  ? filter.key === 'campusId' ? campuses.find((campus) => campus.id === value)?.name ?? value : filterOptions(filter).find((option) => option.value === value)?.label ?? value
                  : t('serviceDesk.list.anyValue');
                return html`<${MenuItem} key=${filter.key} text=${t(`serviceDesk.list.filterLabels.${filter.key}`)} contextualText=${label} hasSubMenu=${true}
                  onClick=${() => setOpenFilterKey(filter.key)} />`;
              })}
              ${activeCount > 0
                ? html`<${MenuItem} text=${t('serviceDesk.list.clearFilters')} icon="IconX" onClick=${() => {
                    onChange(Object.fromEntries(filterList.map((filter) => [filter.key, null])));
                    setIsOpen(false);
                  }} />`
                : null}
            `}
      <//>
    <//>
  </div>`;
}

/**
 * @param {{ filters: InboxFilters, views: InboxView[], counts: Record<string, number>, items: InboxItem[] | null, status: 'loading' | 'ready' | 'error',
 *   selectedKey: string | null, teams: Array<{ id: string, name: string }>, campuses: Array<{ id: string, name: string }>, hasNextPage: boolean, isLoadingMore: boolean,
 *   onFiltersChange: (changes: Partial<InboxFilters>) => void, onSelect: (item: InboxItem) => void, onRetry: () => void, onLoadMore: () => void,
 *   searchText: string, onSearchTextChange: (value: string) => void }} props
 */
export function ServiceInboxList({
  filters, views, counts, items, status, selectedKey, teams, campuses, hasNextPage, isLoadingMore, onFiltersChange, onSelect, onRetry, onLoadMore, searchText, onSearchTextChange,
}) {
  const teamAnchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isTeamMenuOpen, setIsTeamMenuOpen] = useState(false);
  const selectedTeam = teams.find((team) => team.id === filters.teamId) ?? teams[0] ?? null;
  return html`<section class="service-inbox-list" aria-label=${t('serviceDesk.list.title')}>
    <div class="service-inbox-tabs" role="tablist" aria-label=${t('serviceDesk.list.tabsLabel')}>
      ${views.map((view) => html`<button
        key=${view}
        type="button"
        role="tab"
        class="service-inbox-tab"
        aria-selected=${filters.view === view ? 'true' : 'false'}
        data-active=${filters.view === view ? 'true' : undefined}
        data-view=${view}
        onClick=${() => onFiltersChange({ view })}
      >
        <${Icon} name=${VIEW_ICONS[view]} size=${14} />
        <span>${t(`serviceDesk.views.${view}`)}</span>
        ${counts[view] !== undefined && view !== 'closed' ? html`<span class="pill" data-alert=${view === 'atRisk' && counts[view] > 0 ? 'true' : undefined}>${counts[view]}</span>` : null}
      </button>`)}
    </div>
    <div class="service-inbox-toolbar">
      <label class="service-search">
        <${Icon} name="IconSearch" size=${14} />
        <input
          class="service-search-input"
          type="search"
          value=${searchText}
          placeholder=${t('serviceDesk.list.searchPlaceholder')}
          aria-label=${t('serviceDesk.list.searchPlaceholder')}
          onInput=${(/** @type {Event} */ event) => onSearchTextChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
        />
      </label>
      ${filters.view === 'team' && teams.length > 0
        ? html`<div>
            <button type="button" ref=${teamAnchorRef} class="service-filter-button" onClick=${() => setIsTeamMenuOpen(!isTeamMenuOpen)} aria-haspopup="menu">
              <${Icon} name="IconUsersGroup" size=${14} /><span>${selectedTeam?.name ?? ''}</span><${Icon} name="IconChevronDown" size=${12} />
            </button>
            <${Dropdown} anchorRef=${teamAnchorRef} isOpen=${isTeamMenuOpen} onClose=${() => setIsTeamMenuOpen(false)} width=${220} role="menu">
              <${DropdownMenuItems}>
                ${teams.map((team) => html`<${MenuItem} key=${team.id} text=${team.name} selected=${team.id === selectedTeam?.id}
                  onClick=${() => {
                    onFiltersChange({ teamId: team.id });
                    setIsTeamMenuOpen(false);
                  }} />`)}
              <//>
            <//>
          </div>`
        : null}
      <${InboxFilterMenu} filters=${filters} onChange=${onFiltersChange} campuses=${campuses} />
    </div>
    <div class="service-inbox-items">
      ${status === 'error'
        ? html`<div class="service-empty" data-testid="service-inbox-error">
            <${EmptyState} icon="IconAlertTriangle" title=${t('serviceDesk.list.errorTitle')} subtitle=${t('serviceDesk.list.errorDescription')}
              action=${html`<${Button} title=${t('serviceDesk.list.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${onRetry} />`} />
          </div>`
        : status === 'loading' && !items
          ? html`<div class="service-inbox-skeleton">${[0, 1, 2, 3, 4].map((index) => html`<div key=${index} class="service-inbox-skeleton-row">
              <${Skeleton} width=${36} height=${36} radius=${18} /><div><${Skeleton} width="60%" height=${12} /><${Skeleton} width="85%" height=${10} /></div>
            </div>`)}</div>`
          : (items ?? []).length === 0
            ? html`<div class="service-empty" data-testid="service-inbox-empty">
                <${EmptyState} icon=${VIEW_ICONS[filters.view]} title=${t(`serviceDesk.list.empty.${filters.view}`)}
                  subtitle=${countActiveInboxFilters(filters) > 0 || filters.q ? t('serviceDesk.list.emptyFiltered') : t('serviceDesk.list.emptyHint')} />
              </div>`
            : html`<ul class="service-inbox-ul">
                ${(items ?? []).map((item) => html`<${InboxRow} key=${inboxItemKey(item)} item=${item} isSelected=${inboxItemKey(item) === selectedKey} onSelect=${() => onSelect(item)} />`)}
              </ul>
              ${hasNextPage ? html`<div class="service-load-more"><${LightButton} title=${t('serviceDesk.list.loadMore')} icon="IconChevronDown" disabled=${isLoadingMore} onClick=${onLoadMore} /></div>` : null}`}
    </div>
  </section>`;
}
